import Grid from '@material-ui/core/Grid';
import React, { useState, useEffect } from 'react';
import IconButton from '@material-ui/core/IconButton';
import EyeIcon from 'mdi-material-ui/EyeCircle';
import Paper from '@material-ui/core/Paper';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import { CSVLink } from 'react-csv';
import Button from '@material-ui/core/Button';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import { useHistory } from 'react-router-dom';

import { isUserRole } from '../../utils/user';
import UsersTable from '../../components/Tables/Users';
import { setSelectedUser } from '../../store/features/usersTable';
import UserDetails from '../../components/Dialogs/User';
import * as constants from '../../constants';

const csvHeaders = [
  { label: 'Display Name', key: 'displayName' },
  { label: 'Email', key: 'userId' },
  { label: 'Officer Level', key: 'officerLevel' },
  { label: 'Status', key: 'status' }
];

const UsersContent = () => {
  const [ data, setData ] = useState([]);
  const [ csvData, setCsvData ] = useState({});
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const usersById = useSelector(state => state.users.byId);
  const selectedUser = useSelector(state => {
    if(!state.usersTable.selectedUser) return null;
    else return usersById[state.usersTable.selectedUser];
  });
  const selectedUsers = useSelector(state => state.usersTable.selectedUsers);

  useEffect(() => {
    const tempData = [];
    for(const user of Object.values(usersById)) {
      if(user.isDeleted) continue;
      const officerLevel = constants.officerLevels[user.officerLevel];
      tempData.push({
        ...user,
        role: constants.user.roleText[user.role],
        status: constants.user.statusText[user.status],
        officerLevel: officerLevel ? officerLevel.text : 'None', 
        id: user.userId,
        actions: actions(user.userId)
      });
    }
    setData(tempData);
  },[ usersById ]);

  useEffect(() => {
    setCsvData(selectedUsers.map(id => {
      const user = usersById[id];
      const officerLevel = constants.officerLevels[user.officerLevel];
      return {
        ...user,
        role: constants.user.roleText[user.role],
        status: constants.user.statusText[user.status],
        officerLevel: officerLevel ? officerLevel.text : 'None', 
      }
    }));
  }, [selectedUsers]);

  const actions = (id) => {
    return (
      <span className={classes.actions}>
        <IconButton onClick={() => {
          dispatch(setSelectedUser({ selectedUser: id }))
        }}>
          <EyeIcon />
        </IconButton>
      </span>
    )
  }

  const handleCreateUser = () => history.push('/map/users/create');

  const isAdmin = isUserRole({ admin: true });

  return (
    <div className={classes.root}>
      <div className={classes.scrollable}>
        <Paper className={classes.paper}>
          <List>
            <ListItem>
              <ListItemText 
                primary='Users'
              />
              {
                isAdmin && (
                  <ListItemSecondaryAction>
                    <Button
                      color='primary'
                      variant='contained'
                      onClick={handleCreateUser}
                    >
                      Create
                    </Button>
                  </ListItemSecondaryAction>
                )
              }
            </ListItem>
            <ListItem>
              <UsersTable
                data={data}
              />
            </ListItem>
            <ListItem>
              <ListItemSecondaryAction>
                <Button
                  color='primary'
                  variant='contained'
                  disabled={Object.values(csvData).length === 0}
                >
                  {
                    Object.values(csvData).length > 0 ? (
                      <CSVLink 
                        className={classes.link} 
                        headers={csvHeaders} 
                        data={csvData} 
                        filename={`users_export.csv`}
                      >
                        { `Export ${Object.values(csvData).length}` }
                      </CSVLink>
                    ) : (
                      'Export 0'                      
                    )
                  }
                </Button>
              </ListItemSecondaryAction>
            </ListItem>
          </List>
        </Paper>
      </div>
      {
        Boolean(selectedUser) &&
        <UserDetails 
          user={selectedUser}
          isOpen={Boolean(selectedUser)}
          onClose={() => dispatch(setSelectedUser({ selectedUser: null }))}
        />
      }
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
    position: 'relative'
  },
  scrollable: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    top: 0,
    overflowX: 'hidden',
    overflowY: 'auto'
  },
  exportContainer: {
    display: 'flex', 
    justifyContent: 'flex-end' 
  },
  link: {
    color: theme.palette.common.white,
    textDecoration: 'none'
  },
  paper: {
    margin: theme.spacing(3),
    padding: theme.spacing(3)
  },
  buttonContainer: {
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'flex-end'
  }
}));

export default UsersContent;